import { Fragment, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  api,
  type AuditEntry,
  type AuditFilterOptions,
  type AuditPage,
  type AuditRetentionInfo,
} from '../api/client'
import { fmtDate, fmtDateTime } from '../utils/datetime'

const PAGE_SIZES = [25, 50, 100, 200]

/**
 * How a row with no logged-in actor authenticated (U1).
 *
 * This column used to read "(unauthenticated)" for every such row, which for a `/slack/*` post
 * is FALSE — those requests are authenticated by HMAC signature rather than by cookie, and
 * fail-closed — and it implied an attack. It also destroyed the one signal worth alarming on,
 * because a genuine forgery attempt looked identical to ordinary Slack traffic.
 *
 * Three real states plus `not_recorded` for rows written before the evidence was captured. A
 * historical row is NEVER shown as verified: its signature evidence was never stored, so
 * claiming it verified would assert a verification nobody performed.
 */
function ActorAuthBadge({ state }: { state: string }) {
  const styles: Record<string, { text: string; color: string; title: string }> = {
    slack_signature_verified: {
      text: 'Slack validated',
      color: '#4caf50',
      title:
        'Slack signed this request and the HMAC verified against the stored signing secret. ' +
        'Authenticated by signature rather than by cookie — not anonymous traffic.',
    },
    slack_signature_failed: {
      text: 'Slack signature FAILED',
      color: '#f44336',
      title:
        'A Slack signature was PRESENT and did not verify. Either the stored signing secret ' +
        'does not belong to the app that sent this, or the request did not come from Slack. ' +
        'This is the state worth investigating.',
    },
    unauthenticated: {
      text: '(unauthenticated)',
      color: '#ffa726',
      title: 'Nothing was presented to authenticate this request.',
    },
    not_recorded: {
      text: '(auth not recorded)',
      color: '#9aa0a6',
      title:
        'This row was written before the authentication state was captured. It is deliberately ' +
        'not relabelled — the evidence was never stored, so calling it validated would assert ' +
        'a verification nobody performed.',
    },
  }
  const style = styles[state] ?? styles.unauthenticated
  return (
    <span style={{ color: style.color }} title={style.title}>
      {style.text}
    </span>
  )
}

/**
 * The audit log lives at `/settings/logs?pane=audit`.
 *
 * It is one of four views of "what happened", and keeping it as its own top-level
 * settings entry meant an operator correlating a config change against the
 * application log it caused had to leave the page and come back.
 *
 * This is a REDIRECT rather than a deleted route: the old URL is in bookmarks and
 * in links people have already sent each other, and a dead settings URL landing on
 * the dashboard looks like the audit log was removed.
 */
export function AuditLogPage() {
  return <Navigate to="/settings/logs?pane=audit" replace />
}

/**
 * Admin audit log viewer, rendered as the Audit pane of the log viewer.
 *
 * A CHILD COMPONENT, deliberately. It owns six pieces of state — page, page size,
 * four filters, and a sort pair — plus two effects. Lifting those into `LogViewer`
 * would put the audit log's pagination in the same component as the CloudWatch
 * tail interval, where a filter change and a tail tick share one render path.
 *
 * There is no `<AdminOnly>` gate here any more: `LogViewer` gates all four panes
 * once, and a second gate inside a child that only renders behind the first is
 * dead code that reads like a live protection.
 *
 * Denied actions are shown alongside successful ones and styled distinctly: a
 * rejected write ("someone without admin tried to change the role mapping") is
 * usually more interesting than a successful one, so filtering it out by default
 * would hide the page's most valuable rows.
 */
export function AuditLogPane() {
  const [data, setData] = useState<AuditPage | null>(null)
  const [options, setOptions] = useState<AuditFilterOptions | null>(null)
  const [retention, setRetention] = useState<AuditRetentionInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [actor, setActor] = useState<string>('')
  const [action, setAction] = useState<string>('')
  const [resourceType, setResourceType] = useState<string>('')
  const [statusClass, setStatusClass] = useState<string>('')
  const [q, setQ] = useState<string>('')
  const [sortBy, setSortBy] = useState('ts')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const load = () => {
    setLoading(true)
    api
      .getAuditEntries({
        page,
        page_size: pageSize,
        actor: actor || undefined,
        action: action || undefined,
        resource_type: resourceType || undefined,
        status_class: statusClass || undefined,
        q: q || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      })
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [page, pageSize, actor, action, resourceType, statusClass, sortBy, sortDir])

  useEffect(() => {
    api.getAuditFilterOptions().then(setOptions).catch(() => {})
    api.getAuditRetention().then(setRetention).catch(() => {})
  }, [])

  // Any filter change must reset to page 1 — staying on page 7 of a narrower
  // result set shows an empty table and looks like data loss.
  const setFilter = (fn: (v: string) => void) => (v: string) => {
    fn(v)
    setPage(1)
  }

  const hasActiveFilters = Boolean(actor || action || resourceType || statusClass || q)

  const clearFilters = () => {
    setActor(''); setAction(''); setResourceType(''); setStatusClass(''); setQ(''); setPage(1)
  }

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortBy(col)
      setSortDir('desc')
    }
    setPage(1)
  }

  const statusColor = (s: number | null) => {
    if (s === null) return '#9aa0a6'
    if (s >= 500) return '#ef5350'
    if (s === 401 || s === 403) return '#ffa726'
    if (s >= 200 && s < 300) return '#66bb6a'
    return '#9aa0a6'
  }

  return (
    <div>
      {/* No <h2> here: LogViewer owns the page heading and the pane selector says
          which view this is. A second heading inside a pane reads as a second page. */}
      {retention && (
        <div style={{ fontSize: '0.78rem', color: '#9aa0a6', marginBottom: '0.5rem' }}>
          {retention.total_entries.toLocaleString()} entries · retained{' '}
          {retention.retention_days} days
          {retention.oldest_entry ? ` · oldest ${fmtDate(retention.oldest_entry)}` : ''}
        </div>
      )}

      <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#9aa0a6', lineHeight: 1.5 }}>
        Every state-changing API call, including <strong>denied</strong> ones. Captured in
        middleware rather than per-endpoint, so a new endpoint is audited as soon as it
        exists. Credential values (eAPI keys, SMTP/IMAP passwords, HMAC secrets) are
        recorded as present-but-withheld; everything else, including DIDs and run tokens,
        is stored in full.
      </p>

      {error && (
        <p style={{ color: '#ef5350', fontSize: '0.85rem' }}>{error}</p>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.8rem', alignItems: 'center' }}>
        <select value={actor} onChange={e => setFilter(setActor)(e.target.value)} style={selectStyle}>
          <option value="">All actors</option>
          {options?.actors.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={action} onChange={e => setFilter(setAction)(e.target.value)} style={selectStyle}>
          <option value="">All actions</option>
          {options?.actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={resourceType} onChange={e => setFilter(setResourceType)(e.target.value)} style={selectStyle}>
          <option value="">All resources</option>
          {options?.resource_types.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={statusClass} onChange={e => setFilter(setStatusClass)(e.target.value)} style={selectStyle}>
          <option value="">Any outcome</option>
          <option value="success">Succeeded (2xx)</option>
          <option value="denied">Denied (401/403)</option>
          <option value="error">Failed (5xx)</option>
        </select>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load() } }}
          placeholder="Search path or resource id…"
          style={{ ...selectStyle, minWidth: '220px' }}
        />
        <button onClick={() => { setPage(1); load() }} style={btnStyle}>Search</button>
        <button onClick={clearFilters} style={btnStyle} disabled={!hasActiveFilters}>
          Clear
        </button>
      </div>

      {loading && !data ? (
        <p style={{ color: '#9aa0a6' }}>Loading…</p>
      ) : !data || data.items.length === 0 ? (
        // An empty audit log is indistinguishable from a BROKEN one unless the
        // page says why it is empty. "No entries match these filters" is
        // actively misleading when no filters are set, so the two cases are
        // separated: filtered-to-nothing vs genuinely nothing recorded yet.
        hasActiveFilters ? (
          <div style={emptyStyle}>
            <strong>No entries match these filters.</strong>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#9aa0a6' }}>
              {retention && retention.total_entries > 0
                ? `There ${retention.total_entries === 1 ? 'is' : 'are'} ${retention.total_entries.toLocaleString()} entr${retention.total_entries === 1 ? 'y' : 'ies'} in total — clear the filters to see them.`
                : 'The log is also empty overall.'}
            </p>
            <button onClick={clearFilters} style={{ ...btnStyle, marginTop: '0.7rem' }}>
              Clear filters
            </button>
          </div>
        ) : (
          <div style={emptyStyle}>
            <strong>Nothing recorded yet — this is expected on a fresh deploy.</strong>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#9aa0a6', lineHeight: 1.6 }}>
              The audit log records <strong>state-changing</strong> actions only:
              creating or editing a job, resolving a run, changing the role mapping or
              metrics credentials. Browsing pages and viewing dashboards are reads, and
              reads are not recorded — they would outnumber real changes by orders of
              magnitude and bury them.
            </p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#9aa0a6', lineHeight: 1.6 }}>
              Inbound eAPI Webhook deliveries are also excluded. They are machine traffic
              rather than administrative actions, and each one is already recorded in
              full — raw payload included — on its run's detail page.
            </p>
            <p style={{ margin: '0.7rem 0 0', fontSize: '0.82rem', color: '#9aa0a6' }}>
              Edit a job or change a setting and the entry will appear here immediately.
            </p>
          </div>
        )
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#1e1e1e', textAlign: 'left' }}>
                  {[
                    ['ts', 'When'],
                    ['actor_email', 'Actor'],
                    ['action', 'Action'],
                    ['resource_type', 'Resource'],
                    ['http_status', 'Outcome'],
                  ].map(([col, label]) => (
                    <th
                      key={col}
                      onClick={() => toggleSort(col)}
                      style={{ padding: '0.5rem 0.8rem', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      title="Click to sort"
                    >
                      {label}{sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                  <th style={{ padding: '0.5rem 0.8rem' }}>Path</th>
                  <th style={{ padding: '0.5rem 0.8rem' }} />
                </tr>
              </thead>
              <tbody>
                {data.items.map((e: AuditEntry) => (
                  // Keyed Fragment, not a bare <>: each entry renders TWO sibling
                  // rows (the row and its details), so the key must sit on the
                  // wrapper or React cannot reconcile them as one unit.
                  <Fragment key={e.id}>
                    <tr style={{ borderTop: '1px solid #2a2a2a' }}>
                      {/* Full date AND time: this page exists for forensic
                          ordering, and a date-only column cannot order two entries
                          made on the same day. */}
                      <td style={{ padding: '0.5rem 0.8rem', whiteSpace: 'nowrap' }}>{fmtDateTime(e.ts)}</td>
                      <td style={{ padding: '0.5rem 0.8rem' }}>
                        {e.actor_email || <ActorAuthBadge state={e.actor_auth} />}
                        {e.actor_role && (
                          <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#9aa0a6' }}>
                            {e.actor_role}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem 0.8rem', fontFamily: 'monospace' }}>{e.action}</td>
                      <td style={{ padding: '0.5rem 0.8rem' }}>
                        {e.resource_type || '—'}
                        {e.resource_id && (
                          <div style={{ fontSize: '0.7rem', color: '#9aa0a6', fontFamily: 'monospace' }}>
                            {e.resource_id}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem 0.8rem', color: statusColor(e.http_status), fontWeight: 600 }}>
                        {e.http_status ?? '—'}
                        {(e.http_status === 401 || e.http_status === 403) && (
                          <span style={{ marginLeft: '0.3rem', fontSize: '0.7rem' }}>denied</span>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem 0.8rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        <span style={{ color: '#9aa0a6' }}>{e.http_method}</span> {e.http_path}
                      </td>
                      <td style={{ padding: '0.5rem 0.8rem' }}>
                        <button
                          onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                          style={{ ...btnStyle, padding: '0.2rem 0.5rem', fontSize: '0.72rem' }}
                        >
                          {expanded === e.id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {expanded === e.id && (
                      <tr style={{ background: '#161616' }}>
                        <td colSpan={7} style={{ padding: '0.8rem' }}>
                          <div style={{ fontSize: '0.75rem', color: '#9aa0a6', marginBottom: '0.4rem' }}>
                            request_id <code>{e.request_id || '—'}</code>
                            {' · '}ip <code>{e.source_ip || '—'}</code>
                            {' · '}sub <code>{e.actor_sub || '—'}</code>
                          </div>
                          {e.user_agent && (
                            <div style={{ fontSize: '0.72rem', color: '#777', marginBottom: '0.4rem' }}>
                              {e.user_agent}
                            </div>
                          )}
                          <pre style={{ margin: 0, background: '#262626', padding: '0.6rem', borderRadius: '4px', fontSize: '0.72rem', overflow: 'auto', maxHeight: '260px' }}>
                            {e.detail ? JSON.stringify(e.detail, null, 2) : '(no request body)'}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.8rem', flexWrap: 'wrap' }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={btnStyle}>« First</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={btnStyle}>‹ Prev</button>
            <span style={{ fontSize: '0.8rem', color: '#9aa0a6' }}>
              Page {data.page} of {data.total_pages || 1} · {data.total.toLocaleString()} entries
            </span>
            <button onClick={() => setPage(p => Math.min(data.total_pages || 1, p + 1))} disabled={page >= (data.total_pages || 1)} style={btnStyle}>Next ›</button>
            <button onClick={() => setPage(data.total_pages || 1)} disabled={page >= (data.total_pages || 1)} style={btnStyle}>Last »</button>
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1) }}
              style={selectStyle}
            >
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
            </select>
          </div>
        </>
      )}
    </div>
  )
}

const emptyStyle: React.CSSProperties = {
  background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 8,
  padding: '1.25rem', maxWidth: 680, fontSize: '0.9rem',
}

const selectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem', background: '#1e1e1e', color: '#f5f5f5',
  border: '1px solid #333', borderRadius: '4px', fontSize: '0.8rem',
}

const btnStyle: React.CSSProperties = {
  padding: '0.4rem 0.7rem', background: '#262626', color: '#f5f5f5',
  border: '1px solid #333', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer',
}
